import { designTableConstants } from "@/constants/designTableConstants";
import { USER_METADATA_URL } from "@/constants/userMetadata";
import { clientApi, useGetOrganisationApiV1OrganisationsOrgIdGetQuery } from "@/store/clientApi";
import { selectOrgId, setOrgId } from "@/store/slices/orgSlice";
import { RootState, store } from "@/store/store";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

/**
 * A hook that return information about the current users organisation.
 * @param props.skip - Skip any request being made.
 */
const useOrg = (props?: { skip?: boolean }) => {
  const { skip } = props ?? {};
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const orgId = useSelector((state: RootState) => selectOrgId(state));
  const [orgIdLoading, setOrgIdLoading] = useState<boolean>(orgId === undefined);

  useEffect(() => {
    const getOrg = async () => {
      try {
        const accessToken = await getAccessTokenSilently();

        const decoded = jwtDecode<
          JwtPayload & {
            permissions: string[];
            [USER_METADATA_URL]: { organisation: number };
          }
        >(accessToken);

        const decodedOrgId = decoded?.[USER_METADATA_URL]?.organisation;
        if (decodedOrgId !== undefined) {
          store.dispatch(setOrgId(decodedOrgId));
        }
      } catch (_) {
        // silent error in case the user is onboarding
        // and the org is yet to exist
      } finally {
        setOrgIdLoading(false);
      }
    };

    if (orgId === undefined) {
      setOrgIdLoading(true);
      getOrg();
    } else {
      setOrgIdLoading(false);
    }
  }, [getAccessTokenSilently, orgId, t]);

  const {
    data: org,
    error: orgError,
    isLoading: orgIsLoading
  } = useGetOrganisationApiV1OrganisationsOrgIdGetQuery(
    { orgId: orgId ?? -1 },
    {
      skip: orgId === undefined || skip,
      pollingInterval: designTableConstants.POLLING_INTERVAL
    }
  );

  return {
    skipped: skip,
    orgId,
    org,
    error: orgId ? orgError : undefined,
    isLoading: orgIdLoading || (orgId !== undefined && orgIsLoading),
    isOrgIdLoading: orgIdLoading,
    invalidate: () => store.dispatch(clientApi.util.invalidateTags(["organisations"]))
  };
};

export { useOrg };
